// B5's LOADER. The hookless list is `OwnerWordMap`, so a gate CALLS it; this holds the state, and
// nothing here decides anything about a mapping (R28/R29 — the states, the actions and the reach
// sentence are all `src/mosesWordMap.js`'s, read by the door and by the screen alike).
import { useEffect, useState } from "react";
import { OwnerWordMap, type WordMapRow } from "./OwnerWordMap";

export function OwnerWordMapPanel({ onDenied }: { onDenied: () => void }) {
  // R216 — ONLY THE NEWEST READ MAY PAINT. Her desk re-reads on every tap, so two taps inside one
  // round trip leave two answers in flight and the older one lands last.
  const [rows, setRows] = useState<WordMapRow[] | null | undefined>(undefined);
  const [tags, setTags] = useState<string[]>([]);
  const [busyId, setBusyId] = useState("");
  const [seedNote, setSeedNote] = useState("");

  async function load() {
    try {
      const res = await fetch("/api/owner/moses-word-map", { credentials: "include" });
      if (res.status === 403) { onDenied(); return; }
      const data = await res.json().catch(() => null);
      // R39/R47 — read the PAYLOAD, never the status code: this door refuses with `ok: false`.
      if (!data?.ok) { setRows(null); return; }
      setRows(data.rows || []);
      setTags(Array.isArray(data.deliverableTags) ? data.deliverableTags : []);
    } catch { setRows(null); }
  }

  useEffect(() => { load(); }, []);

  async function decide(id: string, action: string) {
    setBusyId(id);
    try {
      const res = await fetch("/api/owner/moses-word-map/decide", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, action }),
      });
      const data = await res.json().catch(() => null);
      if (!data?.ok) { setBusyId(""); return; }
      // The row comes back from the STORE, so her screen shows what really landed rather than what
      // was asked for (R141).
      setRows((prev) => (prev || []).map((r) => (r.id === id ? { ...r, ...data.row } : r)));
    } catch { /* the row keeps the state it had */ }
    setBusyId("");
  }

  async function seed() {
    setSeedNote("");
    try {
      const res = await fetch("/api/owner/moses-word-map/seed", { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => null);
      if (!data?.ok) { setSeedNote("The proposals could not be read just now."); return; }
      setSeedNote(data.added > 0
        ? `${data.added} new proposal${data.added === 1 ? "" : "s"} added.`
        : "Nothing new to propose — every word is already on your list.");
      await load();
    } catch { setSeedNote("The proposals could not be read just now."); }
  }

  return (
    <>
      <div className="owner-bar">
        <p className="owner-page__note">
          What members asked for that reached no seal, and which of your seals it should reach.
          Every one carries the passage from your own book. Nothing binds until you approve it.
        </p>
        <button type="button" className="button button--small" onClick={seed}>Load new proposals</button>
      </div>
      {seedNote ? <p className="owner-note">{seedNote}</p> : null}
      <OwnerWordMap rows={rows} busyId={busyId} onDecide={decide} deliverableTags={tags} />
    </>
  );
}
